import {
    Component,
    Input,
    AfterViewInit,
    ElementRef,
    ChangeDetectorRef,
    Output,
    EventEmitter,
    HostListener
} from '@angular/core';

@Component({
    selector: 'tooltip-content',
    template: `
        <div
            class="tooltip {{ placement }}"
            [style.top]="top + 'px'"
            [style.left]="left + 'px'"
            [class.in]="isIn"
            [class.fade]="isFade"
            role="tooltip"
            data-testing="tooltip"
        >
            <div class="tooltip-arrow"></div>
            <div
                class="tooltip-inner"
                [style.max-width]="maxWidth + 'px' || 'none'"
            >
                <ng-content></ng-content>
                {{ content }}
            </div>
        </div>
    `,
    standalone: false
})
export class TooltipContentComponent implements AfterViewInit {
    @Input()
    hostElement: HTMLElement;
    @Input()
    content: string;
    @Input()
    placement: 'top' | 'bottom' | 'left' | 'right' = 'bottom';
    @Input()
    animation = true;

    @Output()
    focusLeaveEvent = new EventEmitter();
    @Output()
    focusEnterEvent = new EventEmitter();

    // -------------------------------------------------------------------------
    // Properties
    // -------------------------------------------------------------------------

    top = -100000;
    left = -100000;
    isIn = false;
    isFade = false;
    maxWidth: number = null;

    // -------------------------------------------------------------------------
    // Constructor
    // -------------------------------------------------------------------------

    constructor(
        private elementRef: ElementRef,
        private changeDetectorRef: ChangeDetectorRef
    ) {}

    // -------------------------------------------------------------------------
    // Lifecycle callbacks
    // -------------------------------------------------------------------------

    @HostListener('mouseleave')
    @HostListener('focusout')
    onFocusLeave() {
        this.focusLeaveEvent.emit();
    }

    @HostListener('mouseenter')
    @HostListener('focusin')
    onFocusEnter() {
        this.focusEnterEvent.emit();
    }

    ngAfterViewInit(): void {
        this.show();
        this.changeDetectorRef.detectChanges();
    }

    // -------------------------------------------------------------------------
    // Public Methods
    // -------------------------------------------------------------------------

    public show(): void {
        if (!this.hostElement) {
            return;
        }

        // Stretch the width to fit the immediate child element's width to prevent overflow.
        // Done before positioning elements, ignored if not explicitly set to numeric value.
        const computedMaxWidth = parseInt(
            this.getStyle(this.elementRef.nativeElement, 'max-width')
        );
        if (computedMaxWidth) {
            this.maxWidth = this.computeMaxWidth(this.elementRef.nativeElement);
        }

        const position = this.positionElements(
            this.hostElement,
            this.elementRef.nativeElement.children[0],
            this.placement
        );
        this.top = position.top;
        this.left = position.left;
        this.isIn = true;
        if (this.animation) {
            this.isFade = true;
        }
    }

    hide(): void {
        this.top = -100000;
        this.left = -100000;
        this.isIn = true;
        if (this.animation) {
            this.isFade = false;
        }
        this.maxWidth = null;
    }

    // -------------------------------------------------------------------------
    // Private Methods
    // -------------------------------------------------------------------------

    private positionElements(
        hostEl: HTMLElement,
        targetEl: HTMLElement,
        positionStr: string,
        appendToBody = false
    ): { top: number; left: number } {
        const positionStrParts = positionStr.split('-');
        const pos0 = positionStrParts[0];
        const pos1 = positionStrParts[1] || 'center';
        const hostElPos = appendToBody
            ? this.offset(hostEl)
            : this.position(hostEl);
        const targetElWidth = this.maxWidth || targetEl.offsetWidth;
        const targetElHeight = targetEl.offsetHeight;
        const shiftWidth: any = {
            center: (): number =>
                hostElPos.left + hostElPos.width / 2 - targetElWidth / 2,
            left: (): number => hostElPos.left,
            right: (): number => hostElPos.left + hostElPos.width
        };

        const shiftHeight: any = {
            center: (): number =>
                hostElPos.top + hostElPos.height / 2 - targetElHeight / 2,
            top: (): number => hostElPos.top,
            bottom: (): number => hostElPos.top + hostElPos.height
        };

        let targetElPos: { top: number; left: number };
        switch (pos0) {
            case 'right':
                targetElPos = {
                    top: shiftHeight[pos1](),
                    left: shiftWidth[pos0]()
                };
                break;

            case 'left':
                targetElPos = {
                    top: shiftHeight[pos1](),
                    left: hostElPos.left - targetElWidth
                };
                break;

            case 'bottom':
                targetElPos = {
                    top: shiftHeight[pos0](),
                    left: shiftWidth[pos1]()
                };
                break;

            case 'center':
                targetElPos = {
                    top: shiftHeight[pos0](),
                    left: shiftWidth[pos1]()
                };
                break;

            default:
                targetElPos = {
                    top: hostElPos.top - targetElHeight,
                    left: shiftWidth[pos1]()
                };
                break;
        }

        return targetElPos;
    }

    private position(nativeEl: HTMLElement): {
        width: number;
        height: number;
        top: number;
        left: number;
    } {
        let offsetParentBCR = { top: 0, left: 0 };
        const elBCR = this.offset(nativeEl);
        const offsetParentEl = this.parentOffsetEl(nativeEl);
        if (offsetParentEl !== window.document) {
            offsetParentBCR = this.offset(offsetParentEl);
            offsetParentBCR.top +=
                offsetParentEl.clientTop - offsetParentEl.scrollTop;
            offsetParentBCR.left +=
                offsetParentEl.clientLeft - offsetParentEl.scrollLeft;
        }

        const boundingClientRect = nativeEl.getBoundingClientRect();
        return {
            width: boundingClientRect.width || nativeEl.offsetWidth,
            height: boundingClientRect.height || nativeEl.offsetHeight,
            top: elBCR.top - offsetParentBCR.top,
            left: elBCR.left - offsetParentBCR.left
        };
    }

    private offset(nativeEl: any): {
        width: number;
        height: number;
        top: number;
        left: number;
    } {
        const boundingClientRect = nativeEl.getBoundingClientRect();
        return {
            width: boundingClientRect.width || nativeEl.offsetWidth,
            height: boundingClientRect.height || nativeEl.offsetHeight,
            top:
                boundingClientRect.top +
                (window.pageYOffset ||
                    window.document.documentElement.scrollTop),
            left:
                boundingClientRect.left +
                (window.pageXOffset ||
                    window.document.documentElement.scrollLeft)
        };
    }

    private getStyle(nativeEl: HTMLElement, cssProp: string): string {
        if ((nativeEl as any).currentStyle) {
            // IE
            return (nativeEl as any).currentStyle[cssProp];
        }

        if (window.getComputedStyle) {
            return (window.getComputedStyle(nativeEl) as any)[cssProp];
        }

        // finally try and get inline style
        return (nativeEl.style as any)[cssProp];
    }

    private isStaticPositioned(nativeEl: HTMLElement): boolean {
        return (this.getStyle(nativeEl, 'position') || 'static') === 'static';
    }

    private parentOffsetEl(nativeEl: HTMLElement): any {
        let offsetParent: any = nativeEl.offsetParent || window.document;
        while (
            offsetParent &&
            offsetParent !== window.document &&
            this.isStaticPositioned(offsetParent)
        ) {
            offsetParent = offsetParent.offsetParent;
        }
        return offsetParent || window.document;
    }

    private computeMaxWidth(nativeEl: HTMLElement): number {
        const childWidth = nativeEl.children[0].clientWidth;
        let computedMaxWidth = childWidth;

        const maxChildWidth = this.getMaxWidthOfChildElements(
            nativeEl.children[0] as HTMLElement
        );
        if (maxChildWidth > childWidth) {
            const padding = parseInt(
                this.getStyle(
                    nativeEl.querySelector('.tooltip-inner'),
                    'padding-right'
                )
            );
            computedMaxWidth = maxChildWidth + padding;
        }

        return computedMaxWidth;
    }

    private getMaxWidthOfChildElements(targetEl: HTMLElement): number {
        let maxWidth = targetEl.offsetWidth;

        Array.from(targetEl.children).forEach(function (child) {
            if (child.scrollWidth > maxWidth) {
                maxWidth = child.scrollWidth;
            }
        });

        return maxWidth;
    }
}
